import type { Attempt } from './round.ts';
import { compareAttempts, type AttemptComparison } from './scoring.ts';
import type { MatchConfig } from './rules.ts';

export type PlayerSlot = 'a' | 'b';

export interface RoundRecord {
  readonly a: Attempt;
  readonly b: Attempt;
  readonly winner: AttemptComparison;
}

export interface MatchState {
  readonly phase: 'in_progress' | 'complete';
  readonly scores: Readonly<Record<PlayerSlot, number>>;
  readonly history: readonly RoundRecord[];
  readonly winner: PlayerSlot | null;
}

export const EMPTY_MATCH_STATE: MatchState = {
  phase: 'in_progress',
  scores: { a: 0, b: 0 },
  history: [],
  winner: null,
};

export type MatchRecordResult =
  | { ok: true; state: MatchState }
  | { ok: false; reason: string; state: MatchState };

/**
 * Records one completed round's attempts and returns the next match state.
 * Rejects further rounds once a player has already clinched the match, and
 * rejects exceeding the configured maximum round count.
 */
export function recordRoundResult(
  state: MatchState,
  a: Attempt,
  b: Attempt,
  config: MatchConfig,
): MatchRecordResult {
  if (state.phase === 'complete') {
    return { ok: false, reason: 'match is already complete', state };
  }
  if (state.history.length >= config.maxRounds) {
    return { ok: false, reason: 'maximum round count already reached', state };
  }

  const winner = compareAttempts(a, b);
  const scores: Record<PlayerSlot, number> = { ...state.scores };
  if (winner !== 'tie') scores[winner] += 1;

  const history = [...state.history, { a, b, winner }];
  const matchWinner: PlayerSlot | null =
    scores.a >= config.roundWinsToTakeMatch ? 'a' : scores.b >= config.roundWinsToTakeMatch ? 'b' : null;
  const reachedRoundLimit = history.length >= config.maxRounds;

  const phase: MatchState['phase'] = matchWinner !== null || reachedRoundLimit ? 'complete' : 'in_progress';
  const finalWinner: PlayerSlot | null =
    matchWinner ?? (phase === 'complete' ? (scores.a === scores.b ? null : scores.a > scores.b ? 'a' : 'b') : null);

  return { ok: true, state: { phase, scores, history, winner: finalWinner } };
}
